import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, MapPin } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import Calculator from "./Calculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Tracking() {
  const [tid, setTid] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tid.trim().length < 6) { setError("Enter a valid tracking number (min 6 chars)."); return; }
    setLoading(true); setResult(null);
    try {
      const res = await axios.get(`${API}/track/${encodeURIComponent(tid.trim())}`);
      setResult(res.data);
    } catch (err) {
      setError("Tracking number not found.");
    } finally { setLoading(false); }
  };

  return (
    <div id="track" className="card-soft rounded-3xl p-6 sm:p-8" data-testid="tracking-panel">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E31B23]">
          <Search size={18} className="text-white" />
        </span>
        <div>
          <h3 className="font-display text-xl font-bold text-[#1F1F1F]">Track a shipment</h3>
          <p className="text-xs text-[#9ca3af]">Try e.g. XG7788AB</p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        <Input data-testid="track-input" placeholder="Enter tracking number" value={tid}
          onChange={(e) => setTid(e.target.value)} className="h-12 bg-white border-black/10 text-[#1F1F1F] placeholder:text-[#9ca3af] focus-visible:ring-[#E31B23]" />
        <button type="submit" data-testid="track-btn" disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5A189A] hover:bg-[#3D0A6E] px-7 py-3 text-sm font-semibold text-white disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Track
        </button>
      </form>

      {error && <p className="mt-3 text-sm text-[#E31B23]" data-testid="track-error">{error}</p>}

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="track-result" className="mt-6">
            <div className="mb-4 flex items-center justify-between rounded-2xl border border-black/5 bg-[#F5F5F7] p-4">
              <div>
                <div className="text-xs text-[#9ca3af]">{result.origin} → {result.destination}</div>
                <div className="font-display text-lg font-bold text-[#1F1F1F]">{result.status}</div>
              </div>
              <span className="rounded-full bg-[#5A189A]/10 px-3 py-1 text-xs font-medium text-[#5A189A]">{result.service}</span>
            </div>

            <Progress value={result.progress} className="h-1.5 bg-black/10 [&>div]:bg-[#E31B23]" />
            <div className="mt-1 flex justify-between text-[11px] text-[#9ca3af]">
              <span>ETA {fmtDate(result.eta)}</span><span>{result.progress}%</span>
            </div>

            <div className="mt-5">
              {result.timeline.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className={`h-3 w-3 rounded-full ${
                      step.status === "done" ? "bg-[#E31B23]" : step.status === "current" ? "bg-white ring-4 ring-[#E31B23]/30 border border-[#E31B23]" : "bg-black/15"
                    }`} />
                    {i < result.timeline.length - 1 && (
                      <span className={`w-px flex-1 ${step.status === "done" ? "bg-[#E31B23]/50" : "bg-black/10"}`} style={{ minHeight: 30 }} />
                    )}
                  </div>
                  <div className="pb-4">
                    <div className={`text-sm font-semibold ${step.status === "pending" ? "text-[#9ca3af]" : "text-[#1F1F1F]"}`}>{step.title}</div>
                    <div className="flex items-center gap-1 text-xs text-[#9ca3af]">
                      <MapPin size={11} /> {step.location} {step.time && <span>· {fmtDate(step.time)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Tools() {
  return (
    <section id="tools" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#E31B23]">
            <span className="h-px w-8 bg-[#E31B23]" /> Ship in seconds
          </div>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold leading-tight text-[#1F1F1F] sm:text-5xl">
            Quote it. Book it. Track it.
          </h2>
          <p className="mt-4 max-w-xl text-[#4b5563]">
            Real pricing and real-time status the moment you need it — no sales calls, no spreadsheets.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <Reveal delay={0.05}><Calculator /></Reveal>
          <Reveal delay={0.12}><Tracking /></Reveal>
        </div>
      </div>
    </section>
  );
}
